import _ from "lodash"
import React, { useState } from "react"
import {
	Button,
	Chip,
	Divider as DividerMui,
	Grid,
	MenuItem,
	TextField,
	Typography,
	Checkbox,
	FormControlLabel,
	InputLabel
} from "@material-ui/core"

import useCustomStyles from "@/styles/custom"
import {
	Divider,
	ActionDialog,
	Notification,
	InfoDialog,
	Loading,
	TextCopy,
	PopConfirm
} from "@/components"

import ApiService from "@/services/Api"
import TeamService from "@/services/Team"

import InvitePendingCard from "@/pages/Admin/Attendant/components/InvitePendingCard"
import UserInInstanceCard from "@/pages/Admin/Attendant/components/UserInInstanceCard"

import AdminInvitePendingSkeleton from "@/skeletons/Admin/InvitePending"

import useValidation, { ErrorType } from "@/hooks/useValidation"
import useDidMount from "@/hooks/useDidMount"
import useStyles from "@/pages/Admin/Attendant/styles"

import { formatEmail } from "@/utils/mask"
import { getErrorName } from "@/utils/response"
import { onlyLettersNumbersAndAccents } from "@/utils/string"

import {
	Team,
	UserInInstance as IUserInInstance,
	UserStatus
} from "@/protocols/team"
import useSubscriptionLimits from "@/hooks/useSubscriptionLimits"
import UpsellDialog from "@/components/UpsellDialog"

type InviteTokenDataProps = {
	openDialog: boolean
	invitation_link?: string
	name?: string
	email?: string
}

type InvitePendingDataProps = {
	id: number
	recipient_name: string
	recipient_email: string
	invite_link: string
}

type TeamDataProps = Team & {
	instanceId?: number
	selected: boolean
}

type UserInInstanceData = Omit<IUserInInstance, "teams" | "id" | "role_code" | "status"> & {
	id?: number
	role_code?: string
	status?: UserStatus
	teams: TeamDataProps[]
}

type UserInInstance = UserInInstanceData & {
	id: number
	status: UserStatus
}

type UserInvited = UserInInstance & {
	invitation_link: string
}

const Attendant: React.FC = () => {
	const customClasses = useCustomStyles()
	const {
		validation,
		clearValidation,
		triggerValidation
	} = useValidation()
	const classes = useStyles()
	const userQuantityLimit = useSubscriptionLimits("quantity", "user")

	const [inviteTokenData, setInviteTokenData] = useState<InviteTokenDataProps>({
		openDialog: false
	})

	const [usersInInstance, setUsersInInstance] = useState<UserInInstance[]>([])
	const [usersInInstanceLoading, setUsersInInstanceLoading] = useState<boolean>(false)

	const [usersInvited, setUsersInvited] = useState<UserInvited[]>([])
	const [usersInvitedLoading, setUsersInvitedLoading] = useState<boolean>(false)

	const [userInInstance, setUserInInstance] = useState<UserInInstanceData>({
		name: "",
		email: "",
		teams: []
	})

	const [openedUserInInstanceDialog, setOpenedUserInInstanceDialog] = useState<boolean>(false)
	const [userInInstanceLoading, setUserInInstanceLoading] = useState<boolean>(false)

	const [teams, setTeams] = useState<TeamDataProps[]>([])

	const inviteDialogOpen = (invitation_link: string, name: string, email: string) => {
		setInviteTokenData({
			openDialog: true,
			invitation_link,
			name,
			email
		})
		userQuantityLimit.resetLimit()
	}

	const openUserInInstanceDialog = (user: UserInInstance | UserInInstanceData) => {
		setUserInInstance({
			...user,
			teams: teams.map(team => ({
				...team,
				selected: _.findIndex(user.teams, { id: team.id }) >= 0
			}))
		})

		setOpenedUserInInstanceDialog(true)
	}

	const handleUserInInstanceDataChange = (key: string, value: unknown) => {
		clearValidation(key)

		setUserInInstance({
			...userInInstance,
			[key]: value
		})
	}

	const getUsersInvited = async () => {
		setUsersInvitedLoading(true)

		try {
			const { data } = await ApiService.get("/users/invited")

			setUsersInvited(data)
		} catch (error) {
			triggerValidation(error as ErrorType)
		}

		setUsersInvitedLoading(false)
	}

	const getUsersInInstance = async () => {
		if (!usersInInstance) {
			setUsersInInstanceLoading(true)
		}

		const data = await TeamService.getUsersInInstance()

		if (data) {
			setUsersInInstance(data as UserInInstance[])
		}

		setUsersInInstanceLoading(false)
	}

	const handleUserInInstanceInvitationSave = async () => {
		setUserInInstanceLoading(true)

		try {
			const userInInstanceTeams = userInInstance.teams
			const userInInstanceTeamsFiltered = userInInstanceTeams.filter(team => team.selected)

			const { data } = await ApiService
				.post("/user", {
					name: userInInstance.name,
					email: userInInstance.email,
					role_code: userInInstance.role_code,
					teams: userInInstanceTeamsFiltered.map(team => team.id)
				})

			setOpenedUserInInstanceDialog(false)

			inviteDialogOpen(
				data.invitation_link,
				(userInInstance.name as string),
				(userInInstance.email as string)
			)

			getUsersInvited()
			getUsersInInstance()

			Notification.success({ message: "O convite foi gerado com sucesso!" })
		} catch (error) {
			const errorName = getErrorName(error as ErrorType)

			if (errorName === "invitation_request_already_exists_for_this_email") {
				Notification.warning({ message: "Já existe uma solicitação criada para esse email" })
				return
			} else {
				triggerValidation(error as ErrorType)
			}
		}

		setUserInInstanceLoading(false)
	}

	const getTeams = async () => {
		try {
			const { data } = await ApiService
				.get("/team")

			setTeams(data.teams)
		} catch (error) {
			triggerValidation(error as ErrorType)
		}
	}

	const handleTeamCheckboxSelected = (teamIndex: number) => {
		const userInInstanceTeams = userInInstance.teams

		userInInstanceTeams[teamIndex].selected = !userInInstanceTeams[teamIndex].selected

		setUserInInstance({
			...userInInstance,
			teams: userInInstanceTeams
		})
	}

	const handleUserInInstanceSave = async () => {
		setUserInInstanceLoading(true)

		try {
			const userInInstanceTeams = userInInstance.teams
			const userInInstanceTeamsFiltered = userInInstanceTeams.filter(team => team.selected)

			await ApiService
				.put(`/user/${userInInstance.id}`, {
					name: userInInstance.name,
					email: userInInstance.email,
					role_code: userInInstance.role_code,
					teams: userInInstanceTeamsFiltered.map(team => team.id)
				})

			setOpenedUserInInstanceDialog(false)

			getUsersInInstance()

			Notification.success({ message: "O usuário atualizado com sucesso!" })
		} catch (error) {
			triggerValidation(error as ErrorType)
		}

		setUserInInstanceLoading(false)
	}

	const handleUserInInstanceClose = async () => {
		setOpenedUserInInstanceDialog(false)
	}

	const handleDeleteUser = async (user: UserInInstanceData) => {
		PopConfirm.open({
			title: `Excluir ${user.status === "invited" ? "convite" : "usuário"}`,
			description: "Tem certeza? Essa ação é irreversível.",
			confirmButtonText: "EXCLUIR",
			onConfirm: async () => {
				try {
					await ApiService.delete(`/user/${user.id}`)
					getUsersInvited()
					getUsersInInstance()
					userQuantityLimit.resetLimit()
				} catch (error) {
					const errorName = getErrorName(error as ErrorType)

					if (errorName === "HaveAttendencesAssociateWithUser") {
						Notification.warning({ message: "Existe atendimentos vinculados a esse usuario." })
					} else if (errorName === "InstanceOwnerUserCanNotBeDeleted") {
						Notification.warning({ message: "Esse usuário é dono desse Inbox e não pode ser excluído." })
					} else {
						triggerValidation(error as ErrorType)
					}
				}
			}
		})
	}

	const handleBlockUser = async (user: UserInInstanceData) => {
		PopConfirm.open({
			title: `${user.status === "blocked" ? "Desbloquear" : "Bloquear"} usuário`,
			description: "Tem certeza? Essa ação é irreversível.",
			confirmButtonText: `${user.status === "blocked" ? "DESBLOQUEAR" : "BLOQUEAR"}`,
			onConfirm: async () => {
				try {
					await ApiService.put(`/user/${user.id}/block`)

					getUsersInInstance()
				} catch (error) {
					triggerValidation(error as ErrorType)
				}
			}
		})
	}

	useDidMount(() => {
		getUsersInvited()
		getUsersInInstance()
		getTeams()
	})

	return (
		<>
			<Button
				variant="contained"
				className={customClasses.buttonAction}
				onClick={() => {
					userQuantityLimit.mustBlock ? (
						UpsellDialog.open({
							dialogCode: userQuantityLimit.blockCode
						})
					) : (
						openUserInInstanceDialog({
							name: "",
							email: "",
							teams: []
						})
					)
				}}
			>
				CONVIDAR NOVO ATENDENTE
			</Button>

			<Divider size={3} orientation="horizontal"/>

			<Typography
				variant="h4"
				color="textPrimary"
				className={classes.titleText}
			>
				Convites enviados
			</Typography>

			<Divider size={3} orientation="horizontal"/>

			<Loading
				loading={usersInvitedLoading}
				customLoadingElement={<AdminInvitePendingSkeleton/> }>
				<Grid container>
					{
						usersInvited.length
							? usersInvited.map((user, index) => (
								<Grid item xs={12} key={index}>
									<InvitePendingCard
										name={user.name}
										email={user.email}
										invite_link={user.invitation_link}
										handleDelete={() => handleDeleteUser(user)}
									/>

									<Divider size={2} orientation="horizontal" />
								</Grid>
							))
							: <Grid item xs={12}>
								<Typography align="center" variant="h2" className={classes.titleText}>
									Nenhum convite enviado
								</Typography>
							</Grid>
					}
				</Grid>
			</Loading>

			<Divider size={3} orientation="horizontal" />

			<DividerMui />

			<Divider size={3} orientation="horizontal" />

			<Typography
				variant="h4"
				color="textPrimary"
				className={classes.titleText}
			>
				Atendentes Cadastrados
			</Typography>

			<Divider size={3} orientation="horizontal" />

			<Loading
				loading={usersInInstanceLoading}
				customLoadingElement={<AdminInvitePendingSkeleton/> }>
				<Grid container>
					{
						usersInInstance.length
							? usersInInstance.map((userInInstance, index) => (
								<Grid item xs={12} key={index}>
									<UserInInstanceCard
										name={userInInstance.name}
										email={userInInstance.email}
										status={userInInstance.status}
										role_code={userInInstance.role_code}
										teams={userInInstance.teams}
										onClickEditButton={() => openUserInInstanceDialog(userInInstance)}
										onClickDeleteButton={() => handleDeleteUser(userInInstance)}
										onClickBlockButton={() => handleBlockUser(userInInstance)}
									/>

									<Divider size={2} orientation="horizontal" />
								</Grid>
							))
							: <Grid item xs={12}>
								<Typography align="center" variant="h2" className={classes.titleText}>
										Nenhum membro cadastrado
								</Typography>
							</Grid>
					}
				</Grid>
			</Loading>

			<ActionDialog
				title={userInInstance?.id ? "Editar Atendente" : "Convidar novo membro"}
				onSave={() => userInInstance?.id ? handleUserInInstanceSave() : handleUserInInstanceInvitationSave()}
				saveText={userInInstance?.id ? "SALVAR" : "CONVIDAR"}
				onClose={handleUserInInstanceClose}
				openDialog={openedUserInInstanceDialog}
				loading={userInInstanceLoading}
				fullWidth
			>
				<Grid container>
					<Grid item xs={12}>
						<InputLabel className={customClasses.inputLabel}>
                            Nome
						</InputLabel>

						<Divider orientation="horizontal" size={1} />
						<TextField
							id="input-name"
							name="name"
							value={userInInstance.name}
							onChange={
								({ target }) => handleUserInInstanceDataChange("name",
									onlyLettersNumbersAndAccents(target.value))
							}
							variant="outlined"
							placeholder="Ex: José Amilton"
							fullWidth
							helperText={validation.name}
							error={!!validation.name}
						/>
					</Grid>

					<Divider size={2} orientation="horizontal"/>

					<Grid item xs={12}>
						<InputLabel className={customClasses.inputLabel}>
                            Email
						</InputLabel>

						<Divider orientation="horizontal" size={1} />
						<TextField
							id="input-email"
							name="email"
							value={userInInstance.email}
							onChange={
								({ target }) => handleUserInInstanceDataChange("email", formatEmail(target.value))
							}
							variant="outlined"
							placeholder="example@email.com"
							fullWidth
							helperText={validation.email}
							error={!!validation.email}
						/>
					</Grid>

					<Divider size={2} orientation="horizontal"/>

					<Grid item xs={12}>
						<InputLabel className={customClasses.inputLabel}>
                            Tipo de Usuário
						</InputLabel>

						<Divider orientation="horizontal" size={1} />

						<TextField
							id="input-user-type"
							name="role_code"
							select
							placeholder="Tipo de usuário"
							value={userInInstance.role_code || "no-select"}
							onChange={
								({ target }) => target.value !== "no-select" && handleUserInInstanceDataChange("role_code", target.value)
							}
							variant="outlined"
							fullWidth
							helperText={validation.role_code}
							error={!!validation.role_code}
						>
							<MenuItem value={"no-select"} disabled>
                                Tipo de usuário
							</MenuItem>

							<MenuItem value={"admin"}>
								Gestor
							</MenuItem>

							<MenuItem value={"attendant"}>
								Atendente
							</MenuItem>
						</TextField>
					</Grid>

					<Divider size={3} orientation="horizontal"/>

					<Grid item xs={12}>
						<Typography
							color="textPrimary"
							variant="h2"
							className={classes.nameText}
						>
							A quais equipes o atendente pertence?
						</Typography>

						<Grid container className={classes.teamsListContainer}>
							{
								userInInstance.teams.length
									? userInInstance.teams.map((team, index) => (
										<Grid item xs={12} key={index}>
											<FormControlLabel label={team.name} control={
												<Checkbox
													key={index}
													checked={team.selected}
													onChange={() => handleTeamCheckboxSelected(index)}
												/>
											} />
										</Grid>
									))
									: <Grid item xs={12}>
										<Typography align="center" variant="h2" className={classes.noTeamListed}>
												Nenhuma equipe cadastrada.
										</Typography>
									</Grid>
							}
						</Grid>
					</Grid>
				</Grid>
			</ActionDialog>

			<InfoDialog
				title={"Convite foi gerado!"}
				openDialog={inviteTokenData.openDialog}
			>
				<Grid
					container
					alignContent="center"
					className={classes.copyContainer}
				>
					<Grid item xs={12}>
						<Grid container justify="space-between" alignItems="center">
							<Grid item xs={6}>
								<Typography
									color="textPrimary"
									variant="h2"
								>
									{inviteTokenData.name}
								</Typography>

								<Typography
									color="textPrimary"
									variant="caption"
								>
									{inviteTokenData.email}
								</Typography>
							</Grid>

							<Grid item xs={6}>
								<Grid
									container
									justify={"flex-end"}
								>
									<Chip label="Pendente" className={classes.chipPending}/>
								</Grid>
							</Grid>
						</Grid>
					</Grid>

					<Divider size={2} orientation="horizontal" />

					<TextCopy
						text={inviteTokenData.invitation_link as string}
						onSuccessMessage="Link copiado com sucesso!"
					/>
				</Grid>

			</InfoDialog>
		</>
	)
}

export default Attendant
